import { useMutation, useQueryClient } from '@tanstack/vue-query'
import type { AxiosError } from 'axios'
import { editAssignmentsApi, editAssignmentsQueryKeys } from '@/api/resources/edit-assignments'
import { useToastStore } from '@/stores/useToastStore'
import { extractErrorMessage } from '@/utils/errorHandler'

/**
 * "Mark as working" — the editor confirms an edit that was routed back to them, so it stops being
 * provisional and stays theirs. Without a confirmation inside the window the server hands the work
 * back to Ready to Edit.
 */
export function useAcknowledgeEditAssignment() {
    const queryClient = useQueryClient()
    const toastStore = useToastStore()

    return useMutation({
        mutationFn: (revisionId: number) => editAssignmentsApi.acknowledge(revisionId),

        onSuccess: () => {
            toastStore.success('Marked as working', 'This edit is yours — it stays assigned to you.')
            // Toast first, then refresh. Both the app-wide prompt and the proofing banner read the
            // pending query, so one invalidation clears both surfaces.
            queryClient.invalidateQueries({ queryKey: editAssignmentsQueryKeys.all })
        },

        onError: (error: AxiosError) => {
            toastStore.error(extractErrorMessage(error))
            // A 409 means it is no longer waiting on this editor (already confirmed elsewhere, or
            // released back to the queue) — drop the stale row from the prompt.
            queryClient.invalidateQueries({ queryKey: editAssignmentsQueryKeys.all })
        },
    })
}
